import { useForm, useStore } from "@tanstack/react-form";
import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	notFound,
	stripSearchParams,
	useLoaderData,
	useNavigate,
	useParams,
	useRouteContext,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { cnpj, cpf } from "cpf-cnpj-validator";
import { addDays, format, parse } from "date-fns";
import { isValid } from "date-fns/isValid";
import { useEffect, useState } from "react";
import { graphql, useMutation } from "react-relay";
import axios from "redaxios";
import { fetchQuery } from "relay-runtime";
import { toast } from "sonner";
import { withMask } from "use-mask-input";
import z from "zod";
import { useOnboardingAnalytics } from "@/analytics/onboarding";
import { Icon } from "@/components/icon";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from "@/components/ui/input-group";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { getReferralCode } from "@/handlers/getReferralCode";
import { environment } from "@/relay/environment";
import type { checkoutOnboardingUpdateMutation } from "./__generated__/checkoutOnboardingUpdateMutation.graphql";
import type { checkoutPlansQuery } from "./__generated__/checkoutPlansQuery.graphql";
import type { checkoutSubscriptionCreateMutation } from "./__generated__/checkoutSubscriptionCreateMutation.graphql";

const defaultSearch = {
	plan: "scarf-basic",
} as const;

const states = [
	{ value: "AC", label: "Acre" },
	{ value: "AL", label: "Alagoas" },
	{ value: "AP", label: "Amapá" },
	{ value: "AM", label: "Amazonas" },
	{ value: "BA", label: "Bahia" },
	{ value: "CE", label: "Ceará" },
	{ value: "DF", label: "Distrito Federal" },
	{ value: "ES", label: "Espírito Santo" },
	{ value: "GO", label: "Goiás" },
	{ value: "MA", label: "Maranhão" },
	{ value: "MT", label: "Mato Grosso" },
	{ value: "MS", label: "Mato Grosso do Sul" },
	{ value: "MG", label: "Minas Gerais" },
	{ value: "PA", label: "Pará" },
	{ value: "PB", label: "Paraíba" },
	{ value: "PR", label: "Paraná" },
	{ value: "PE", label: "Pernambuco" },
	{ value: "PI", label: "Piauí" },
	{ value: "RJ", label: "Rio de Janeiro" },
	{ value: "RN", label: "Rio Grande do Norte" },
	{ value: "RS", label: "Rio Grande do Sul" },
	{ value: "RO", label: "Rondônia" },
	{ value: "RR", label: "Roraima" },
	{ value: "SC", label: "Santa Catarina" },
	{ value: "SP", label: "São Paulo" },
	{ value: "SE", label: "Sergipe" },
	{ value: "TO", label: "Tocantins" },
];

export const Route = createFileRoute("/_logged/onboarding/$club/checkout")({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			plan: z.enum(["scarf-basic", "scarf-pro"]).default("scarf-basic"),
		}),
	),
	loaderDeps: ({ search }) => [search],
	search: {
		middlewares: [stripSearchParams(defaultSearch)],
	},
	loader: async ({ deps: [{ plan }] }) => {
		const planSlug = plan || "scarf-basic";

		const referralCode = await getReferralCode();
		const data = await fetchQuery<checkoutPlansQuery>(
			environment,
			graphql`
        query checkoutPlansQuery($slug: String!) {
          scarfPlan(slug: $slug) {
            name
            slug
            formattedPrice
            rawPrice
          }
        }
      `,
			{ slug: planSlug },
		).toPromise();

		if (!data?.scarfPlan) {
			throw notFound();
		}

		return { plan: data?.scarfPlan!, referralCode };
	},
});

function RouteComponent() {
	const navigate = useNavigate({ from: Route.fullPath });
	const { session } = useRouteContext({ from: "__root__" });
	const { club: clubSlug } = useParams({ from: Route.id });
	const { club: onboardingClub } = useRouteContext({
		from: "/_logged/onboarding/$club",
	});
	const { plan, referralCode } = useLoaderData({ from: Route.id });
	const { capture, trackError, posthog } = useOnboardingAnalytics(
		"checkout",
		clubSlug,
	);

	const [createSubscription] =
		useMutation<checkoutSubscriptionCreateMutation>(graphql`
	  mutation checkoutSubscriptionCreateMutation($input: ClubSubscriptionCreateInput!) {
			clubSubscriptionCreate(input: $input) {
				id
			}
		}
	`);

	const [updateClub] = useMutation<checkoutOnboardingUpdateMutation>(graphql`
      mutation checkoutOnboardingUpdateMutation($club: ID!, $payload: UpdateClubInput!) {
        updateClub(id: $club, payload: $payload) {
          id
        }
      }
    `);

	const form = useForm({
		defaultValues: {
			customerType: "company",
			customer: {
				name: "",
				document: "",
				phone: {
					type: "MOBILE",
					number: "",
				},
			},
			card: {
				number: "",
				holderName: "",
				expDate: "",
				cvv: "",
			},
			billingAddress: {
				zip: "",
				street: "",
				number: "",
				neighborhood: "",
				complement: "",
				city: "",
				state: "",
			},
		},
		validators: {
			onChange: z
				.object({
					customerType: z.enum(["individual", "company"]),
					customer: z.object({
						name: z.string().min(3, "Nome deve ter pelo menos 3 caracteres"),
						document: z.string(),
						phone: z.union([
							z.object({
								type: z.literal("MOBILE"),
								number: z
									.string()
									.length(15, "Campo Inválido")
									.regex(
										/^\([0-9]{2}\) [0-9]{5}-[0-9]{4}$/,
										"Formato Inválido",
									),
							}),
							z.object({
								type: z.literal("HOME"),
								number: z
									.string()
									.length(14, "Campo Inválido")
									.regex(
										/^\([0-9]{2}\) [0-9]{4}-[0-9]{4}$/,
										"Formato Inválido",
									),
							}),
						]),
					}),
					card: z.object({
						number: z
							.string()
							.length(19, "Campo Inválido")
							.regex(/^[0-9]{4}-[0-9]{4}-[0-9]{4}-[0-9]{4}$/, "Campo Inválido"),
						holderName: z.string().min(3, "Nome Inválido"),
						expDate: z
							.string({ error: "Campo Inválido" })
							.length(5, "Campo Inválido")
							.refine(
								(val) => isValid(parse(val, "MM/yy", new Date())),
								"Data Inválida",
							),
						cvv: z.string().min(3, "Campo Inválido").max(4, "Campo Inválido"),
					}),
					billingAddress: z.object({
						zip: z
							.string()
							.length(9, "Campo Inválido")
							.regex(/^[0-9]{5}-[0-9]{3}$/, "Campo Inválido"),
						street: z.string().min(3, "Campo Inválido"),
						number: z.string().min(1, "Campo não pode ser vazio"),
						neighborhood: z.string().min(3, "Campo Inválido"),
						complement: z.string(),
						city: z.string().min(3, "Campo Inválido"),
						state: z
							.string()
							.length(2, "Campo Inválido")
							.refine(
								(val) => states.map((s) => s.value).includes(val),
								"Campo Inválido",
							),
					}),
				})
				.superRefine((val, ctx) => {
					if (val.customerType === "individual") {
						if (!cpf.isValid(val.customer.document)) {
							ctx.addIssue({
								code: z.ZodIssueCode.custom,
								path: ["customer.document"],
								message: "CPF inválido",
							});
						}
					} else {
						if (!cnpj.isValid(val.customer.document)) {
							ctx.addIssue({
								code: z.ZodIssueCode.custom,
								path: ["customer.document"],
								message: "CNPJ inválido",
							});
						}
					}
				}),
		},
		onSubmit: async ({ value }) => {
			try {
				const splitedExpDate = value.card.expDate.split("/");
				const phoneMatch = value.customer.phone.number.match(
					/\((\d{2})\)\s*(\d{4,5})-(\d{4})/,
				);

				if (!phoneMatch) {
					toast.error("Formato de telefone inválido");
					return;
				}

				const phoneDdd = phoneMatch[1];
				const phoneNumber = `${phoneMatch[2]}-${phoneMatch[3]}`;

				const { data } = await axios.post(
					`https://api.pagar.me/core/v5/tokens?appId=${import.meta.env.VITE_PAGARME_KEY}`,
					{
						card: {
							number: value.card.number.replace(/[^0-9]/g, ""),
							holder_name: value.card.holderName,
							exp_month: splitedExpDate[0],
							exp_year: `20${splitedExpDate[1]}`,
							cvv: value.card.cvv.replace(/[^0-9]/g, ""),
						},
						type: "card",
					},
					{
						headers: {
							"Content-Type": "application/json",
						},
					},
				);

				return new Promise((resolve, reject) => {
					createSubscription({
						variables: {
							input: {
								club: clubSlug,
								referralCode: referralCode?.length ? referralCode : undefined,
								customer: {
									email: session?.user?.email!,
									document: value.customer.document.replace(/[^0-9]/g, ""),
									name: value.customer.name,
									type: value.customerType.toUpperCase(),
									phone: {
										ddd: phoneDdd,
										number: phoneNumber,
										type: value.customer.phone.type as "MOBILE" | "HOME",
									},
								},
								card: {
									cardToken: data.id,
									billingAddress: {
										city: value.billingAddress.city,
										complement: value.billingAddress.complement,
										neighborhood: value.billingAddress.neighborhood,
										number: value.billingAddress.number,
										state: value.billingAddress.state,
										street: value.billingAddress.street,
										zipCode: value.billingAddress.zip.replace(/[^0-9]/g, ""),
									},
								},
								paymentMethod: "CREDIT_CARD",
								plan: plan.slug,
							},
						},
						onCompleted: (response, errors) => {
							if (errors?.length) {
								toast.error(errors[0].message);
								reject(new Error(errors[0].message));
								return;
							}
							updateClub({
								variables: {
									club: onboardingClub?.id ?? "",
									payload: {
										configurations: [
											{
												key: "ONBOARDING_COMPLETED",
												value: "1",
											},
										],
									},
								},
								onCompleted: (_clubResponse, clubErrors) => {
									if (clubErrors?.length) {
										toast.error(clubErrors[0].message);
										reject(new Error(clubErrors[0].message));
										return;
									}
									capture("completed", {
										plan_slug: plan.slug,
										plan_price: plan.rawPrice,
										customer_type: value.customerType,
										has_referral_code: Boolean(referralCode?.length),
									});
									posthog.capture("checkout_payment_completed", {
										revenue: plan.rawPrice,
										currency: "BRL",
										plan: plan.slug,
										customer_type: value.customerType,
										referral_code: referralCode || undefined,
									});
									toast.success("Assinatura criada com sucesso!");
									navigate({ to: "/onboarding/$club/launch" });
									resolve(response);
								},
								onError: (error) => {
									trackError("submission", {
										error_message: "onboarding_update_failed",
										plan_slug: plan.slug,
									});
									toast.error("Erro ao finalizar onboarding!");
									reject(error);
								},
							});
						},
						onError: (error) => {
							trackError("submission", {
								error_message: "subscription_creation_failed",
								plan_slug: plan.slug,
							});
							reject(error);
						},
					});
				});
			} catch {
				trackError("submission", {
					error_message: "payment_processing_failed",
					plan_slug: plan.slug,
				});
				toast.error(
					"Não foi possível criar sua assinatura no momento. Tente novamente mais tarde.",
				);
			}
		},
	});

	const zipCode = useStore(
		form.store,
		(state) => state.values.billingAddress.zip,
	);

	const [zipCodeLoaded, setZipCodeLoaded] = useState(false);

	const { data, isPending, isError, isEnabled, isSuccess } = useQuery({
		queryKey: ["zip", zipCode],
		queryFn: () => {
			return axios
				.get(`https://viacep.com.br/ws/${zipCode.replaceAll("-", "")}/json/`)
				.then(({ data }) => data);
		},
		enabled:
			zipCode.length === 9 && new RegExp(/^[0-9]{5}-[0-9]{3}$/).test(zipCode),
	});

	useEffect(() => {
		if (!zipCodeLoaded && data && isSuccess) {
			setZipCodeLoaded(true);
		}

		if (data && isSuccess) {
			form.setFieldValue("billingAddress.street", data.logradouro);
			form.setFieldValue("billingAddress.city", data.localidade);
			form.setFieldValue("billingAddress.neighborhood", data.bairro);
			form.setFieldValue("billingAddress.state", data.uf);
		}
	}, [data, zipCodeLoaded, isSuccess, form.setFieldValue]);

	const daysToFirstCharge =
		referralCode === "baixador" ? 14 : referralCode ? 30 : 7;

	const planDetails = {
		"scarf-basic": {
			name: "Basic",
			description: "O essencial para o seu negócio",
			features: [
				"500 alunos",
				"2 Admins",
				"200 GB de armazenamento",
				"300 GB de banda",
				"Domínio e branding próprios",
			],
			price: "R$ 497/mês",
		},
		"scarf-pro": {
			name: "Pro",
			description: "Para escalar seu negócio",
			features: [
				"2.000 Membros",
				"5 Admins",
				"500 GB de armazenamento",
				"1 TB de banda",
				"Feeds ilimitados",
				"Automações e Filtros Avançados",
			],
			price: "R$ 997/mês",
		},
	};

	const currentPlan = planDetails[plan.slug as keyof typeof planDetails];

	return (
		<div className="min-h-screen w-full flex items-start justify-center p-6 overflow-x-hidden max-w-full">
			<form
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					form.handleSubmit();
				}}
				className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-5xl overflow-x-hidden"
			>
				<Card className="p-6 w-full mb-6 md:hidden order-1">
					<div className="flex flex-col">
						<span className="uppercase text-xs/5">Seu Plano</span>

						<div className="flex flex-col mt-4">
							<span className="text-2xl font-semibold">{currentPlan.name}</span>
							<span className="text-muted-foreground">
								{currentPlan.description}
							</span>
						</div>

						<div className="flex flex-col mt-4">
							<span className="line-through text-muted-foreground">
								{currentPlan.price}
							</span>
							<span className="text-4xl font-bold text-destructive mt-1">
								R$ 0,00
							</span>
							<span className="text-sm text-destructive mt-1">
								Teste {daysToFirstCharge} dias grátis
							</span>
						</div>

						<div className="flex flex-col gap-1 mt-4">
							{currentPlan.features.map((feature) => (
								<div
									key={feature}
									className="flex flex-row items-center justify-start gap-3 text-gray-600"
								>
									<Icon name="Check" className="size-4 text-emerald-600" />
									{feature}
								</div>
							))}
						</div>

						<Separator className="my-4" />

						<div className="flex flex-col gap-2 text-sm">
							<div className="flex items-center gap-2">
								<Icon
									name="Calendar"
									className="size-4 text-muted-foreground"
								/>
								<span>
									<strong>Primeira cobrança:</strong>{" "}
									{format(addDays(new Date(), daysToFirstCharge), "dd/MM/yyyy")}
								</span>
							</div>
							<div className="flex items-center gap-2">
								<Icon name="Shield" className="size-4 text-muted-foreground" />
								<span>
									<strong>Período de teste:</strong> {daysToFirstCharge} dias
									grátis
								</span>
							</div>
						</div>
					</div>
				</Card>

				<div className="md:col-span-2 order-2 md:order-1">
					<Card className="p-6 w-full mb-6">
						<div className="flex flex-col gap-6">
							<span className="text-lg/6 font-semibold mb-3">
								Dados Pessoais
							</span>

							<form.Field name="customerType">
								{(field) => (
									<Field>
										<FieldLabel>Tipo de Cliente</FieldLabel>
										<RadioGroup
											value={field.state.value}
											onValueChange={field.handleChange}
										>
											<div className="flex items-center gap-3">
												<RadioGroupItem value="company" />

												<Label>Pessoa Jurídica</Label>
											</div>

											<div className="flex items-center gap-3">
												<RadioGroupItem value="individual" />

												<Label>Pessoa Física</Label>
											</div>
										</RadioGroup>

										{field.state.meta.isTouched &&
										field.state.meta.errors[0] ? (
											<FieldError errors={field.state.meta.errors} />
										) : null}
									</Field>
								)}
							</form.Field>

							<form.Subscribe selector={(state) => [state.values.customerType]}>
								{([customerType]) => (
									<form.Field name="customer.name">
										{(field) => (
											<Field>
												<FieldLabel htmlFor={field.name}>
													{customerType === "individual"
														? "Seu Nome"
														: "Nome da Empresa"}
												</FieldLabel>

												<Input
													id={field.name}
													name={field.name}
													value={field.state.value}
													onBlur={field.handleBlur}
													onChange={(e) => field.handleChange(e.target.value)}
													placeholder="Nome"
													aria-invalid={
														!!(
															field.state.meta.isTouched &&
															field.state.meta.errors[0]
														)
													}
												/>

												{field.state.meta.isTouched &&
												field.state.meta.errors[0] ? (
													<FieldError errors={field.state.meta.errors} />
												) : null}
											</Field>
										)}
									</form.Field>
								)}
							</form.Subscribe>

							<form.Subscribe selector={(state) => [state.values.customerType]}>
								{([customerType]) => (
									<form.Field name="customer.document">
										{(field) => (
											<Field>
												<FieldLabel htmlFor={field.name}>
													Documento (
													{customerType === "individual" ? "CPF" : "CNPJ"})
												</FieldLabel>

												<Input
													id={field.name}
													name={field.name}
													value={field.state.value}
													onBlur={field.handleBlur}
													onChange={(e) => field.handleChange(e.target.value)}
													placeholder="Documento"
													aria-invalid={
														!!(
															field.state.meta.isTouched &&
															field.state.meta.errors[0]
														)
													}
													ref={
														customerType === "individual"
															? withMask("999.999.999-99", {
																	showMaskOnHover: false,
																})
															: withMask("99.999.999/9999-99", {
																	showMaskOnHover: false,
																})
													}
												/>

												{field.state.meta.isTouched &&
												field.state.meta.errors[0] ? (
													<FieldError errors={field.state.meta.errors} />
												) : null}
											</Field>
										)}
									</form.Field>
								)}
							</form.Subscribe>

							<Field>
								<FieldLabel>Telefone</FieldLabel>
								<div className="grid grid-cols-3 gap-3">
									<form.Field name="customer.phone.type">
										{(field) => (
											<Select
												name={field.name}
												value={field.state.value}
												onValueChange={(e) => field.handleChange(e)}
											>
												<SelectTrigger className="w-full">
													<SelectValue />
												</SelectTrigger>

												<SelectContent>
													<SelectItem value="MOBILE">Celular</SelectItem>
													<SelectItem value="HOME">Fixo</SelectItem>
												</SelectContent>
											</Select>
										)}
									</form.Field>

									<form.Subscribe
										selector={(state) => [state.values.customer.phone.type]}
									>
										{([phoneType]) => (
											<form.Field name="customer.phone.number">
												{(field) => (
													<>
														<Input
															id={field.name}
															name={field.name}
															value={field.state.value}
															onBlur={field.handleBlur}
															onChange={(e) =>
																field.handleChange(e.target.value)
															}
															placeholder="Telefone"
															className="col-span-2"
															aria-invalid={
																!!(
																	field.state.meta.isTouched &&
																	field.state.meta.errors[0]
																)
															}
															ref={
																phoneType === "MOBILE"
																	? withMask("(99) 99999-9999")
																	: withMask("(99) 9999-9999")
															}
														/>

														{field.state.meta.isTouched &&
														field.state.meta.errors[0] ? (
															<FieldError errors={field.state.meta.errors} />
														) : null}
													</>
												)}
											</form.Field>
										)}
									</form.Subscribe>
								</div>
							</Field>
						</div>

						<Separator />

						<div className="flex flex-col gap-6">
							<span className="text-lg/6 font-semibold mb-3">
								Dados do Cartão
							</span>

							<form.Field name="card.number">
								{(field) => (
									<Field>
										<FieldLabel>Número do Cartão</FieldLabel>

										<Input
											id={field.name}
											name={field.name}
											value={field.state.value}
											onBlur={field.handleBlur}
											onChange={(e) => field.handleChange(e.target.value)}
											placeholder="Número do Cartão"
											ref={withMask("9999-9999-9999-9999", {
												showMaskOnHover: false,
											})}
											aria-invalid={
												!!(
													field.state.meta.isTouched &&
													field.state.meta.errors[0]
												)
											}
										/>

										{field.state.meta.isTouched &&
										field.state.meta.errors[0] ? (
											<FieldError errors={field.state.meta.errors} />
										) : null}
									</Field>
								)}
							</form.Field>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
								<form.Field name="card.cvv">
									{(field) => (
										<Field>
											<FieldLabel>Código de Segurança (CVV)</FieldLabel>

											<Input
												id={field.name}
												name={field.name}
												value={field.state.value}
												onBlur={field.handleBlur}
												onChange={(e) => field.handleChange(e.target.value)}
												placeholder="CVV"
												ref={withMask("999", { showMaskOnHover: false })}
												aria-invalid={
													!!(
														field.state.meta.isTouched &&
														field.state.meta.errors[0]
													)
												}
											/>

											{field.state.meta.isTouched &&
											field.state.meta.errors[0] ? (
												<FieldError errors={field.state.meta.errors} />
											) : null}
										</Field>
									)}
								</form.Field>

								<form.Field name="card.expDate">
									{(field) => (
										<Field>
											<FieldLabel>Data de Expiração</FieldLabel>

											<Input
												id={field.name}
												name={field.name}
												value={field.state.value}
												onBlur={field.handleBlur}
												onChange={(e) => field.handleChange(e.target.value)}
												placeholder="MM/AA"
												ref={withMask("99/99", { showMaskOnHover: false })}
												aria-invalid={
													!!(
														field.state.meta.isTouched &&
														field.state.meta.errors[0]
													)
												}
											/>

											{field.state.meta.isTouched &&
											field.state.meta.errors[0] ? (
												<FieldError errors={field.state.meta.errors} />
											) : null}
										</Field>
									)}
								</form.Field>
							</div>

							<form.Field name="card.holderName">
								{(field) => (
									<Field>
										<FieldLabel>Nome do Titular</FieldLabel>

										<Input
											id={field.name}
											name={field.name}
											value={field.state.value}
											onBlur={field.handleBlur}
											onChange={(e) => field.handleChange(e.target.value)}
											placeholder="Nome do Titular"
											aria-invalid={
												!!(
													field.state.meta.isTouched &&
													field.state.meta.errors[0]
												)
											}
										/>

										{field.state.meta.isTouched &&
										field.state.meta.errors[0] ? (
											<FieldError errors={field.state.meta.errors} />
										) : null}
									</Field>
								)}
							</form.Field>
						</div>

						<Separator />

						<div className="flex flex-col gap-6">
							<span className="text-lg/6 font-semibold mb-3">
								Endereço de Cobrança
							</span>

							<form.Field name="billingAddress.zip">
								{(field) => (
									<Field>
										<FieldLabel>CEP</FieldLabel>

										<InputGroup>
											<InputGroupInput
												id={field.name}
												name={field.name}
												value={field.state.value}
												onBlur={field.handleBlur}
												onChange={(e) => field.handleChange(e.target.value)}
												placeholder="CEP"
												ref={withMask("99999-999", { showMaskOnHover: false })}
												aria-invalid={
													!!(
														field.state.meta.isTouched &&
														field.state.meta.errors[0]
													) ||
													(isEnabled && isError)
												}
											/>

											{isEnabled && isPending && (
												<InputGroupAddon align="inline-end">
													<Spinner />
												</InputGroupAddon>
											)}

											{isEnabled && isError && (
												<InputGroupAddon align="inline-end">
													<Icon
														name="CircleX"
														className="text-destructive size-3"
													/>
												</InputGroupAddon>
											)}
										</InputGroup>

										{field.state.meta.isTouched &&
										field.state.meta.errors[0] ? (
											<FieldError errors={field.state.meta.errors} />
										) : null}
									</Field>
								)}
							</form.Field>

							{zipCodeLoaded && (
								<>
									<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
										<form.Field name="billingAddress.state">
											{(field) => (
												<Field>
													<FieldLabel>Estado</FieldLabel>

													<Input
														id={field.name}
														name={field.name}
														value={field.state.value}
														onBlur={field.handleBlur}
														onChange={(e) => field.handleChange(e.target.value)}
														placeholder="Estado"
														aria-invalid={
															!!(
																field.state.meta.isTouched &&
																field.state.meta.errors[0]
															)
														}
													/>

													{field.state.meta.isTouched &&
													field.state.meta.errors[0] ? (
														<FieldError errors={field.state.meta.errors} />
													) : null}
												</Field>
											)}
										</form.Field>

										<form.Field name="billingAddress.city">
											{(field) => (
												<Field>
													<FieldLabel>Cidade</FieldLabel>

													<Input
														id={field.name}
														name={field.name}
														value={field.state.value}
														onBlur={field.handleBlur}
														onChange={(e) => field.handleChange(e.target.value)}
														placeholder="Cidade"
														aria-invalid={
															!!(
																field.state.meta.isTouched &&
																field.state.meta.errors[0]
															)
														}
													/>

													{field.state.meta.isTouched &&
													field.state.meta.errors[0] ? (
														<FieldError errors={field.state.meta.errors} />
													) : null}
												</Field>
											)}
										</form.Field>

										<form.Field name="billingAddress.neighborhood">
											{(field) => (
												<Field>
													<FieldLabel>Bairro</FieldLabel>

													<Input
														id={field.name}
														name={field.name}
														value={field.state.value}
														onBlur={field.handleBlur}
														onChange={(e) => field.handleChange(e.target.value)}
														placeholder="Bairro"
														aria-invalid={
															!!(
																field.state.meta.isTouched &&
																field.state.meta.errors[0]
															)
														}
													/>

													{field.state.meta.isTouched &&
													field.state.meta.errors[0] ? (
														<FieldError errors={field.state.meta.errors} />
													) : null}
												</Field>
											)}
										</form.Field>
									</div>

									<form.Field name="billingAddress.street">
										{(field) => (
											<Field>
												<FieldLabel>Endereço</FieldLabel>

												<Input
													id={field.name}
													name={field.name}
													value={field.state.value}
													onBlur={field.handleBlur}
													onChange={(e) => field.handleChange(e.target.value)}
													placeholder="Endereço"
													aria-invalid={
														!!(
															field.state.meta.isTouched &&
															field.state.meta.errors[0]
														)
													}
												/>

												{field.state.meta.isTouched &&
												field.state.meta.errors[0] ? (
													<FieldError errors={field.state.meta.errors} />
												) : null}
											</Field>
										)}
									</form.Field>

									<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
										<form.Field name="billingAddress.number">
											{(field) => (
												<Field>
													<FieldLabel>Número</FieldLabel>

													<Input
														id={field.name}
														name={field.name}
														value={field.state.value}
														onBlur={field.handleBlur}
														onChange={(e) => field.handleChange(e.target.value)}
														placeholder="Número"
														aria-invalid={
															!!(
																field.state.meta.isTouched &&
																field.state.meta.errors[0]
															)
														}
													/>

													{field.state.meta.isTouched &&
													field.state.meta.errors[0] ? (
														<FieldError errors={field.state.meta.errors} />
													) : null}
												</Field>
											)}
										</form.Field>

										<form.Field name="billingAddress.complement">
											{(field) => (
												<Field>
													<FieldLabel>Complemento (opcional)</FieldLabel>

													<Input
														id={field.name}
														name={field.name}
														value={field.state.value}
														onBlur={field.handleBlur}
														onChange={(e) => field.handleChange(e.target.value)}
														placeholder="Complemento"
														aria-invalid={
															!!(
																field.state.meta.isTouched &&
																field.state.meta.errors[0]
															)
														}
													/>

													{field.state.meta.isTouched &&
													field.state.meta.errors[0] ? (
														<FieldError errors={field.state.meta.errors} />
													) : null}
												</Field>
											)}
										</form.Field>
									</div>
								</>
							)}
						</div>

						<Separator />

						<div className="flex flex-col gap-1 items-center justify-center">
							<form.Subscribe
								selector={(state) => [state.isSubmitting, state.errors]}
							>
								{([isSubmitting, _errors]) => (
									<div className="flex items-center justify-center w-full">
										<Button
											type="submit"
											disabled={Boolean(isSubmitting)}
											className="flex items-center gap-2 w-full"
										>
											Confirmar
											{isSubmitting && <Spinner />}
										</Button>
									</div>
								)}
							</form.Subscribe>

							<span className="text-center text-xs/5 text-muted-foreground text-balance">
								Fique tranquilo, você terá {daysToFirstCharge} dias para testar
								gratuitamente. A primeira cobrança será somente em{" "}
								{format(addDays(new Date(), daysToFirstCharge), "dd/MM/yyyy")}
							</span>
						</div>
					</Card>
				</div>

				<div className="md:col-span-1 order-1 md:order-2 hidden md:block">
					<Card className="p-6 w-full">
						<div className="flex flex-col">
							<span className="uppercase text-xs/5">Seu Plano</span>

							<div className="flex flex-col items-center text-center mt-4">
								<span className="text-2xl font-semibold">
									{currentPlan.name}
								</span>
								<span className="text-muted-foreground">
									{currentPlan.description}
								</span>
								<div className="flex flex-col items-center mt-4">
									<span className="line-through text-muted-foreground text-xl">
										{currentPlan.price}
									</span>
									<span className="text-4xl font-bold text-destructive mt-1">
										R$ 0,00
									</span>
									<span className="text-sm text-destructive mt-1">
										Teste {daysToFirstCharge} dias grátis
									</span>
								</div>
							</div>

							<Separator className="my-6" />

							<div className="flex flex-col gap-2 mb-4">
								<span className="font-semibold">Incluído:</span>
								{currentPlan.features.map((feature) => (
									<div
										key={feature}
										className="flex flex-row items-center justify-start gap-3 text-gray-600 text-sm"
									>
										<Icon name="Check" className="size-4 text-emerald-600" />
										{feature}
									</div>
								))}
							</div>

							<Separator className="my-4" />

							<div className="flex flex-col gap-3 text-sm">
								<div className="flex items-center gap-2">
									<Icon
										name="Calendar"
										className="size-4 text-muted-foreground"
									/>
									<span>
										<strong>Primeira cobrança:</strong>{" "}
										{format(
											addDays(new Date(), daysToFirstCharge),
											"dd/MM/yyyy",
										)}
									</span>
								</div>
								<div className="flex items-center gap-2">
									<Icon
										name="Shield"
										className="size-4 text-muted-foreground"
									/>
									<span>
										<strong>Período de teste:</strong> {daysToFirstCharge} dias
										grátis
									</span>
								</div>
							</div>
						</div>
					</Card>
				</div>
			</form>
		</div>
	);
}
